import React, { FC, useEffect, useMemo, useState } from 'react';

import { Icon } from '@iconify/react';
import { Box, Button, IconButton, Skeleton, Typography } from '@mui/material';
import Payment from '@solidgate/react-sdk';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import PaymentError from '~/components/molecules/PaymentError';
import { usePaymentFormLogic } from '~/components/organisms/SolidCheckout/hooks/usePaymentFormLogic';
import {
  PaymentApi,
  UpdatePaymentTokenReason,
} from '~/components/organisms/SolidCheckout/types';
import useUserCountry from '~/hooks/useUserCountry';
import { buildInlinePaymentFormStyles } from '~/projects/polyllm-chat/components/ChatPaywallModal/components/InlinePaymentForm/styles';
import { ProjectBehaviorStrategy } from '~/projects/ProjectBehaviorStrategy';
import analytics from '~/services/analytics';
import {
  AnalyticPaymentMethods,
  SOLID_PAYMENT_METHOD_MAPPER,
} from '~/types/payment';

type Props = {
  onSuccessPayment: () => void | Promise<void>;
  isDark: boolean;
};

/** Constrain a Solidgate-injected payment button to a fixed height slot. */
const PAYMENT_BTN_HEIGHT = 55;

const RedemptionCheckout: FC<Props> = ({ onSuccessPayment, isDark }) => {
  const { t } = useTranslation('general');
  const countryCode = useUserCountry('');

  const [view, setView] = useState<'methods' | 'card'>('methods');
  const [isCardFormReady, setIsCardFormReady] = useState(false);

  const paymentApi: PaymentApi = useMemo(
    () => ({
      createPaymentIntentCard: (geo_country) =>
        Api.updatePaymentMethodCard({
          geo_country,
          token_type: UpdatePaymentTokenReason.REDEMPTION,
        }),
      createPaymentIntentPaypal: () => Api.updatePaymentMethodPaypal(),
    }),
    [],
  );

  const handlePaymentSuccess = async (): Promise<void> => {
    analytics.trackEvent('platform checkout - payment update success');
    await onSuccessPayment();
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    analytics.trackEvent('platform checkout - error', {
      method,
      error: message,
    });
  };

  const handlePaymentSubmit = (method: AnalyticPaymentMethods): void => {
    analytics.trackEvent('platform checkout - submit', {
      method,
    });
  };

  const {
    isLoading,
    paymentError,
    merchantData,
    paypalBtnRef,
    appleContainerRef,
    googleContainerRef,
    handleCloseError,
    handlePaymentSuccess: onSolidgateSuccess,
    handlePaymentFail: onSolidgateFail,
    handlePaymentSubmit: onSolidgateSubmit,
  } = usePaymentFormLogic({
    paymentApi,
    onPaymentSuccess: handlePaymentSuccess,
    onPaymentFail: handlePaymentFail,
    onPaymentSubmit: handlePaymentSubmit,
  });

  useEffect(() => {
    analytics.trackEvent('platform checkout - payment update view');
  }, []);

  const formStyles = useMemo(
    () => buildInlinePaymentFormStyles(isDark),
    [isDark],
  );

  const legalAddress =
    ProjectBehaviorStrategy.getPaymentConfigByCountry(
      countryCode,
    ).getCompanyAddress(t);

  const walletButtonSlotSx = {
    height: PAYMENT_BTN_HEIGHT,
    overflow: 'hidden',
    borderRadius: '100px',
    lineHeight: 0,
    '& > *': {
      width: '100% !important',
      height: `${PAYMENT_BTN_HEIGHT}px !important`,
    },
  };

  const isMethodsView = view === 'methods';
  const isCardView = view === 'card';

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderRadius: '16px',
        maxWidth: 480,
        width: '100%',
        margin: '0 auto',
        px: { xs: '20px', sm: '28px' },
        py: { xs: '24px', sm: '28px' },
      }}
    >
      {isLoading && <Loader />}

      {paymentError && (
        <PaymentError
          paymentError={paymentError}
          handleCloseError={handleCloseError}
        />
      )}

      <Typography
        component="h3"
        sx={{
          fontWeight: 700,
          fontSize: '20px',
          lineHeight: '28px',
          textAlign: 'center',
          mb: '4px',
        }}
      >
        {t(
          'subscription:payment_form.payment_method_update',
          'Payment Method Update',
        )}
      </Typography>
      <Typography
        sx={{
          fontSize: '13px',
          lineHeight: '18px',
          color: 'text.secondary',
          textAlign: 'center',
          mb: '20px',
        }}
      >
        {t(
          'subscription:payment_form.payment_method_update_desc',
          'Updated method will be used for billing your existing plan',
        )}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: '12px',
          pb: '12px',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: '20px',
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
          {t('total', 'Total')}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
          $0.00 USD
        </Typography>
      </Box>

      <Box sx={{ position: 'relative' }}>
        <Box
          sx={{
            display: isMethodsView ? 'flex' : 'none',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <Box ref={appleContainerRef} sx={walletButtonSlotSx} />
          <Box ref={googleContainerRef} sx={walletButtonSlotSx} />
          <Box
            sx={{
              height: PAYMENT_BTN_HEIGHT,
              overflow: 'hidden',
              borderRadius: '100px',
            }}
          >
            <div
              ref={paypalBtnRef}
              id="paypal-button"
              style={{ height: '100%' }}
            />
          </Box>

          <Button
            fullWidth
            onClick={() => {
              analytics.trackEvent('platform checkout - navigation', {
                method: 'card',
              });
              setView('card');
            }}
            sx={{
              bgcolor: '#3b71f7',
              color: '#fff',
              borderRadius: '100px',
              height: PAYMENT_BTN_HEIGHT,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '15px',
              gap: '8px',
              '&:hover': { bgcolor: '#2d5fe0' },
            }}
          >
            <Icon icon="ph:credit-card-bold" width={18} />
            {t('chat_paywall.credit_card_btn', 'Credit or debit card')}
          </Button>
        </Box>

        <Box sx={{ display: isCardView ? 'block' : 'none' }}>
          <IconButton
            onClick={() => {
              analytics.trackEvent('platform checkout - navigation', {
                method: 'one_pay',
              });
              setView('methods');
            }}
            size="small"
            sx={{
              mb: '12px',
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
              color: 'text.primary',
              borderRadius: '8px',
              width: 32,
              height: 32,
            }}
          >
            <Icon icon="ph:caret-left-bold" width={18} />
          </IconButton>

          {!isCardFormReady && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              <Skeleton
                variant="rounded"
                height={52}
                sx={{ borderRadius: '12px 12px 0 0', transform: 'none' }}
              />
              <Box sx={{ display: 'flex' }}>
                <Skeleton
                  variant="rounded"
                  height={52}
                  sx={{
                    flex: 1,
                    borderRadius: '0 0 0 12px',
                    transform: 'none',
                  }}
                />
                <Skeleton
                  variant="rounded"
                  height={52}
                  sx={{
                    flex: 1,
                    borderRadius: '0 0 12px 0',
                    transform: 'none',
                  }}
                />
              </Box>
              <Skeleton
                variant="rounded"
                height={52}
                sx={{ mt: '8px', borderRadius: '12px', transform: 'none' }}
              />
              <Skeleton
                variant="rounded"
                height={50}
                sx={{ mt: '16px', borderRadius: '100px', transform: 'none' }}
              />
            </Box>
          )}
        </Box>

        {merchantData && !paymentError && (
          <Box
            sx={{
              opacity: isCardView && isCardFormReady ? 1 : 0,
              transition: 'opacity 0.2s ease',
              pointerEvents: isCardView ? 'auto' : 'none',
              position: isCardView && isCardFormReady ? 'static' : 'absolute',
              inset: 0,
              width: '100%',
            }}
          >
            <Payment
              applePayContainerRef={appleContainerRef}
              googlePayContainerRef={googleContainerRef}
              merchantData={merchantData}
              applePayButtonParams={{ integrationType: 'js' }}
              formParams={{
                submitButtonText: t('chat_paywall.pay_now', 'Pay now'),
                cardExpiryDatePlaceholder: 'MM/YY',
              }}
              styles={formStyles}
              onFail={(e) =>
                onSolidgateFail(
                  e.message || '',
                  SOLID_PAYMENT_METHOD_MAPPER[e.entity],
                )
              }
              onSuccess={async (e) => onSolidgateSuccess(e.order)}
              onSubmit={(e) =>
                onSolidgateSubmit(SOLID_PAYMENT_METHOD_MAPPER[e.entity])
              }
              onMounted={() => setTimeout(() => setIsCardFormReady(true), 100)}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          mt: '16px',
          color: 'text.secondary',
        }}
      >
        <Icon icon="ph:lock-simple-bold" width={14} />
        <Typography
          sx={{
            fontSize: '12px',
            lineHeight: '16px',
          }}
        >
          {t('payment_form.guarantee', {
            ns: 'subscription',
            defaultValue: 'Guaranteed security payments',
          })}
        </Typography>
      </Box>

      <Typography
        sx={{
          mt: '12px',
          fontSize: '11px',
          lineHeight: '16px',
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        {t('general:payment_processed_by', 'The payment processed by')}{' '}
        {legalAddress}
      </Typography>
    </Box>
  );
};

export default RedemptionCheckout;
